import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useEffect, useState, type ReactElement } from 'react'
import {
  fetchArrivalsBoard,
  groupArrivals,
  type Arrival,
  type ArrivalsBoard,
} from '@metro/station-arrivals'
import styles from './StationArrivalsSection.module.css'

interface Props {
  /** Station NAPTAN id — the arrivals endpoint key. */
  naptanId: string
  /** TfL line id (= schema `line.slug`) the board filters to. */
  tflLineId: string
  /** Display name for the heading, e.g. `Bakerloo`. */
  lineDisplayName: string
  /** Line hex — paints the "Now" cell, per the design. */
  lineColor: string
  /** For the footer caption. */
  stationName: string
}

/** Board refresh cadence — matches the server's 60 s per-station cache. */
const REFRESH_MS = 60_000

/** `Now` under a minute, else `N min` (board convention). */
function timeLabel(seconds: number, t: TFunction<'station-page'>): string {
  return seconds < 60
    ? t('arrivals_time_now')
    : t('arrivals_time_min', { count: Math.round(seconds / 60) })
}

/** TfL suffixes every destination with `Underground Station` — noise on a board. */
function destinationLabel(arrival: Arrival): string {
  const name = arrival.destinationName !== '' ? arrival.destinationName : arrival.towards
  return name.replace(/ Underground Station$/, '')
}

/** `Updated just now` (<30 s) / `Updated N min ago` from the cache stamp. */
function updatedLabel(generatedAt: string, now: Date, t: TFunction<'station-page'>): string {
  const ageMs = now.getTime() - new Date(generatedAt).getTime()
  if (!Number.isFinite(ageMs) || ageMs < 30_000) return t('arrivals_updated_just_now')
  return t('arrivals_updated_min_ago', { count: Math.max(1, Math.round(ageMs / 60_000)) })
}

/**
 * Live arrivals section — Figma 8597:41578 (TASK-699): per-platform
 * columns of the current line's next trains at this station. Data:
 * `@metro/station-arrivals` (the cross-platform client package) over
 * `GET /api/v2/arrivals/station/{naptanId}` — metro-routing holds a 60 s
 * per-station cache of TfL predictions, and the section re-fetches on the same 60 s
 * cadence, so every client of a station costs TfL one request a
 * minute.
 *
 * Fail-safe: fetch failure or an empty board renders the quiet
 * "No live departures" state (functional copy, not a designed state —
 * flagged to design), never a throw.
 */
export function StationArrivalsSection({
  naptanId,
  tflLineId,
  lineDisplayName,
  lineColor,
  stationName,
}: Props): ReactElement {
  const { t } = useTranslation('station-page')
  const [board, setBoard] = useState<ArrivalsBoard | null>(null)

  useEffect(() => {
    let cancelled = false
    const refresh = (): void => {
      fetchArrivalsBoard(naptanId)
        .then((b) => {
          if (!cancelled) setBoard(b)
        })
        .catch(() => {
          if (!cancelled) setBoard({ naptanId, generatedAt: '', arrivals: [] })
        })
    }
    refresh()
    const timer = setInterval(refresh, REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [naptanId])

  const groups = board === null ? [] : groupArrivals(board.arrivals, { lineId: tflLineId })

  return (
    <div className={styles.section}>
      <h2 className={styles.heading}>{t('arrivals_heading', { line: lineDisplayName })}</h2>
      {board !== null && groups.length === 0 && (
        <p className={styles.empty}>{t('arrivals_empty')}</p>
      )}
      {groups.length > 0 && (
        <div className={styles.board}>
          {groups.map((group) => (
            <div key={`${group.heading}-${group.platform}`} className={styles.column}>
              <div className={styles.columnHeader}>
                <span className={styles.direction}>{group.heading}</span>
                {group.platform !== '' && <span className={styles.platform}>{group.platform}</span>}
              </div>
              {group.towards !== '' && (
                <p className={styles.towards}>
                  {t('arrivals_towards', { destination: group.towards })}
                </p>
              )}
              <div className={styles.rows}>
                {group.arrivals.map((arrival, index) => (
                  <div key={arrival.id} className={styles.row}>
                    <span
                      className={styles.time}
                      style={
                        arrival.timeToStationSec < 60 && index === 0
                          ? { color: lineColor }
                          : undefined
                      }
                    >
                      {timeLabel(arrival.timeToStationSec, t)}
                    </span>
                    <span className={styles.trainInfo}>
                      <span className={styles.destination}>{destinationLabel(arrival)}</span>
                      {arrival.currentLocation !== '' && (
                        <span className={styles.location}>{arrival.currentLocation}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {board !== null && board.generatedAt !== '' && (
        <p className={styles.footer}>
          {updatedLabel(board.generatedAt, new Date(), t)} ·{' '}
          {t('arrivals_footer_caption', { station: stationName })}
        </p>
      )}
    </div>
  )
}
